import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  UserX,
  ServerCrash,
  Loader2,
  ArrowRightLeft,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StripeReconcileTab — compares live Stripe subscriptions against the
 * WeMasterminds user table and lets the admin fix drift with a click.
 *
 * Four buckets returned by the backend:
 *   ok            — Stripe agrees with DB (nothing to do)
 *   drift         — user linked, but status/tier/id mismatch
 *   orphan_stripe — Stripe has a sub whose customer isn't linked to any user
 *   orphan_db     — DB has a stripe_subscription_id Stripe doesn't return
 *
 * Fixing drift or linking an orphan: POST /admin/subscriptions/reconcile/apply
 * with { user_id, stripe_subscription_id?, apply_stripe_status: true }.
 * Stripe is treated as the SOURCE OF TRUTH.
 */
export default function StripeReconcileTab({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/subscriptions/reconcile`, {
        headers: getAuthHeader(),
        timeout: 60000,
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch reconcile data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const apply = async ({ user_id, stripe_subscription_id }) => {
    const key = `${user_id}:${stripe_subscription_id || ''}`;
    setApplyingId(key);
    try {
      await axios.post(
        `${API_URL}/api/admin/subscriptions/reconcile/apply`,
        {
          user_id,
          stripe_subscription_id: stripe_subscription_id || undefined,
          apply_stripe_status: true,
        },
        { headers: getAuthHeader() },
      );
      toast.success('Applied — Stripe values copied to DB');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplyingId(null);
    }
  };

  const applyDisabled = (row) =>
    applyingId === `${row.user_id}:${row.stripe_subscription_id || ''}`;

  const counts = data?.counts || {};

  return (
    <div className="space-y-6" data-testid="stripe-reconcile-tab">
      <div className="flex items-center gap-3">
        <ArrowRightLeft className="text-indigo-500" size={22} />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-white">Stripe ↔ DB reconciliation</h2>
          <p className="text-xs text-slate-400">
            Compares live Stripe subscriptions to the users table. Stripe is treated as source of truth.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          data-testid="stripe-reconcile-refresh"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" /> {data ? 'Refresh' : 'Load'}
            </>
          )}
        </Button>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="In sync"
            value={counts.ok}
            icon={<CheckCircle2 size={16} />}
            tone="emerald"
            testid="reconcile-count-ok"
          />
          <SummaryCard
            label="Drift"
            value={counts.drift}
            icon={<AlertTriangle size={16} />}
            tone="amber"
            testid="reconcile-count-drift"
          />
          <SummaryCard
            label="Orphan (Stripe)"
            value={counts.orphan_stripe}
            icon={<UserX size={16} />}
            tone="rose"
            testid="reconcile-count-orphan-stripe"
          />
          <SummaryCard
            label="Orphan (DB)"
            value={counts.orphan_db}
            icon={<ServerCrash size={16} />}
            tone="slate"
            testid="reconcile-count-orphan-db"
          />
        </div>
      )}

      {!data && !loading && (
        <div className="text-center py-16 border border-dashed border-slate-700 rounded-xl text-slate-400">
          Click <strong>Load</strong> to fetch Stripe and compare against the DB.
        </div>
      )}

      {/* Drift table */}
      {data && data.drift?.length > 0 && (
        <Section title="Drift" tone="amber" count={data.drift.length}>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-400 text-left">
                <th className="py-2 pr-2">User</th>
                <th className="pr-2">DB</th>
                <th className="pr-2">Stripe</th>
                <th className="pr-2">Reasons</th>
                <th className="pr-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.drift.map((r) => (
                <tr key={r.user_id} className="border-t border-slate-700/50" data-testid={`drift-row-${r.user_id}`}>
                  <td className="py-2 pr-2 text-white">
                    <div className="font-medium">{r.name || r.email}</div>
                    <div className="text-slate-500">{r.email}</div>
                  </td>
                  <td className="pr-2 text-slate-300">
                    <div>{r.db_tier} / {r.db_status || '—'}</div>
                    <div className="text-slate-500 text-[10px] font-mono truncate max-w-[140px]">
                      {r.db_stripe_subscription_id || '(no sub)'}
                    </div>
                  </td>
                  <td className="pr-2 text-slate-300">
                    <div>{r.expected_tier || '—'} / {r.stripe_status}</div>
                    <div className="text-slate-500 text-[10px] font-mono truncate max-w-[140px]">
                      {r.stripe_subscription_id}
                    </div>
                  </td>
                  <td className="pr-2 text-amber-300 text-[11px]">
                    <ul className="list-disc pl-3">
                      {r.drift_reasons.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="pr-2 text-right">
                    <Button
                      size="sm"
                      onClick={() =>
                        apply({
                          user_id: r.user_id,
                          stripe_subscription_id: r.stripe_subscription_id,
                        })
                      }
                      disabled={applyDisabled(r)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-[11px]"
                      data-testid={`drift-apply-${r.user_id}`}
                    >
                      {applyDisabled(r) ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        'Sync from Stripe'
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Orphan Stripe */}
      {data && data.orphan_stripe?.length > 0 && (
        <Section title="Orphan (in Stripe, not linked in DB)" tone="rose" count={data.orphan_stripe.length}>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-400 text-left">
                <th className="py-2 pr-2">Stripe sub</th>
                <th className="pr-2">Customer email</th>
                <th className="pr-2">Tier / Status</th>
                <th className="pr-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.orphan_stripe.map((r) => (
                <tr key={r.stripe_subscription_id} className="border-t border-slate-700/50">
                  <td className="py-2 pr-2 text-slate-300 font-mono text-[10px]">
                    {r.stripe_subscription_id}
                  </td>
                  <td className="pr-2 text-slate-300">{r.customer_email || '—'}</td>
                  <td className="pr-2 text-slate-300">
                    {r.expected_tier || '—'} / {r.stripe_status}
                  </td>
                  <td className="pr-2 text-right">
                    <a
                      href={`https://dashboard.stripe.com/subscriptions/${r.stripe_subscription_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-[11px]"
                    >
                      Open in Stripe <ExternalLink size={10} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-2">
            Match these customers to a user email in your DB, then link them from the users table
            (the "reset-password / manual sync" step is on the roadmap for one-click linking).
          </p>
        </Section>
      )}

      {/* Orphan DB */}
      {data && data.orphan_db?.length > 0 && (
        <Section title="Orphan (in DB, no Stripe sub returned)" tone="slate" count={data.orphan_db.length}>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-400 text-left">
                <th className="py-2 pr-2">User</th>
                <th className="pr-2">DB tier / status</th>
                <th className="pr-2">Stale sub id</th>
                <th className="pr-2">Hint</th>
              </tr>
            </thead>
            <tbody>
              {data.orphan_db.map((r) => (
                <tr key={r.user_id} className="border-t border-slate-700/50">
                  <td className="py-2 pr-2 text-white">{r.email}</td>
                  <td className="pr-2 text-slate-300">
                    {r.db_tier} / {r.db_status}
                  </td>
                  <td className="pr-2 text-slate-300 font-mono text-[10px]">
                    {r.stripe_subscription_id}
                  </td>
                  <td className="pr-2 text-slate-500">{r.hint}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* OK section — collapsed by default */}
      {data && data.ok?.length > 0 && (
        <details className="border border-slate-700 rounded-xl p-4 bg-slate-900/40">
          <summary className="cursor-pointer text-sm text-emerald-400 font-medium flex items-center gap-2">
            <CheckCircle2 size={16} /> {data.ok.length} subscription{data.ok.length > 1 ? 's' : ''} in sync
          </summary>
          <table className="w-full text-xs mt-3">
            <thead>
              <tr className="text-slate-400 text-left">
                <th className="py-2 pr-2">User</th>
                <th className="pr-2">Tier / Status</th>
                <th className="pr-2">Stripe sub</th>
              </tr>
            </thead>
            <tbody>
              {data.ok.map((r) => (
                <tr key={r.user_id} className="border-t border-slate-700/50">
                  <td className="py-2 pr-2 text-slate-300">{r.email}</td>
                  <td className="pr-2 text-slate-300">
                    {r.db_tier} / {r.db_status}
                  </td>
                  <td className="pr-2 text-slate-500 font-mono text-[10px] truncate max-w-[220px]">
                    {r.stripe_subscription_id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon, tone, testid }) {
  const tones = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
    rose: 'bg-rose-500/10 border-rose-500/30 text-rose-300',
    slate: 'bg-slate-500/10 border-slate-500/30 text-slate-300',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-white">{value ?? 0}</div>
    </div>
  );
}

function Section({ title, tone, count, children }) {
  const border = {
    amber: 'border-amber-500/30',
    rose: 'border-rose-500/30',
    slate: 'border-slate-500/30',
  }[tone] || 'border-slate-700';
  return (
    <div className={`rounded-xl border ${border} bg-slate-900/40 p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <span className="text-xs text-slate-400">({count})</span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}
